import { Children, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Search, X } from "lucide-react";
import { useCreateTask, useTaskPicker } from "../../api/tasks";
import { useFirmwareList } from "../../api/firmware";
import { useResourceBundle } from "../../api/resources";
import { useGroups } from "../../api/groups";
import { useDevices, useDeviceTotal, deviceFilterParams, type DeviceSearchField } from "../../api/devices";
import { useAdminDevices, useAdminDeviceTotal } from "../../api/admin";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { formatRange, totalPagesFor, useCursorPaging } from "../../hooks/useCursorPaging";
import { extractErrorMessage } from "../../api/errors";
import type { Device, StagedConfig } from "../../api/types";
import type { DeviceGroup } from "../../api/groups";
import { DataTable } from "../../components/DataTable";
import { StatusBadge } from "../../components/StatusBadge";
import { getActingTenantId, getRole } from "../../auth/session";

type Step = "firmware" | "target" | "config" | "review";
type TargetType = "all" | "groups" | "devices";
type TaskSource = "existing" | "new";
type TaskTargetDevice = Device & { tenantId?: string };
const DEVICE_TARGET_PAGE_SIZE = 25;
const DEVICE_SEARCH_DEBOUNCE_MS = 300;

function toggleValue(values: string[], value: string): string[] {
  return values.includes(value) ? values.filter((entry) => entry !== value) : [...values, value];
}

function includesQuery(value: string | undefined, query: string): boolean {
  return (value ?? "").toLowerCase().includes(query);
}

function deviceLabel(device: Device | undefined, imei: string): string {
  if (!device) return imei;
  return device.deviceName && device.deviceName !== imei ? `${device.deviceName} (${imei})` : imei;
}

export function TaskCreatePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const scopedTenantId = searchParams.get("tenantId")?.trim() || undefined;
  const actingTenantId = getActingTenantId();
  const isPlatformAdminTask = getRole() === "super_admin" && !scopedTenantId && !actingTenantId;
  const { data: firmwareData } = useFirmwareList({ status: "active", tenantId: scopedTenantId });
  // Tasks are unbounded, so the "existing task" picker loads a page at a
  // time with "Load more" rather than stopping silently at the first 100.
  const taskPicker = useTaskPicker({ tenantId: scopedTenantId });
  const tasksLoading = taskPicker.isLoading;
  // Every group, so a group past the first page can still be targeted.
  const { data: groupsData, isLoading: groupsLoading } = useGroups({ tenantId: scopedTenantId });

  // The device picker searches, filters, pages and counts on the server, so
  // any device in the fleet can be found and selected — not just those on
  // the page that happens to be loaded.
  const [deviceSearch, setDeviceSearch] = useState("");
  const [deviceSearchField, setDeviceSearchField] = useState<DeviceSearchField>("imei");
  const [deviceStatus, setDeviceStatus] = useState("");
  const [ungroupedOnly, setUngroupedOnly] = useState(false);
  const debouncedDeviceSearch = useDebouncedValue(deviceSearch, DEVICE_SEARCH_DEBOUNCE_MS);
  const deviceFilters = {
    search: debouncedDeviceSearch,
    searchField: deviceSearchField,
    status: (deviceStatus || undefined) as "online" | "stale" | "offline" | undefined,
    unallocated: ungroupedOnly,
  };
  const devicePaging = useCursorPaging(
    JSON.stringify([deviceFilterParams(deviceFilters).toString(), actingTenantId, isPlatformAdminTask, scopedTenantId]),
  );
  const { data: devicesData, isLoading: tenantDevicesLoading, isPlaceholderData: tenantDevicesStale } = useDevices(
    { ...deviceFilters, limit: DEVICE_TARGET_PAGE_SIZE, cursor: devicePaging.cursor, tenantId: scopedTenantId },
    { enabled: !isPlatformAdminTask },
  );
  const { data: adminDevicesData, isLoading: adminDevicesLoading, isPlaceholderData: adminDevicesStale } = useAdminDevices({
    enabled: isPlatformAdminTask,
    filters: deviceFilters,
    limit: DEVICE_TARGET_PAGE_SIZE,
    cursor: devicePaging.cursor,
  });
  const tenantDeviceTotal = useDeviceTotal({ ...deviceFilters, tenantId: scopedTenantId }, { enabled: !isPlatformAdminTask });
  const adminDeviceTotal = useAdminDeviceTotal({ enabled: isPlatformAdminTask, filters: deviceFilters });

  const preselectedImeis = useMemo(
    () =>
      (searchParams.get("imeis") ?? "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
    [searchParams],
  );
  const preselectedFirmwareId = searchParams.get("firmwareId") ?? "";
  const preselectedResourceBundleId = searchParams.get("resourceBundleId") ?? "";
  const preselectedResourceBundleVersion = Number(searchParams.get("resourceBundleVersion") ?? "0") || undefined;
  const isResourceTask = Boolean(preselectedResourceBundleId);
  const { data: resourceBundleData } = useResourceBundle(preselectedResourceBundleId, scopedTenantId);
  const resourceBundle = resourceBundleData?.data;
  const resourceBundleVersion = preselectedResourceBundleVersion ?? resourceBundle?.latestVersion;

  const [step, setStep] = useState<Step>("firmware");
  const [taskSource, setTaskSource] = useState<TaskSource>("new");
  const [existingTaskId, setExistingTaskId] = useState("");
  const [name, setName] = useState("");
  const [firmwareId, setFirmwareId] = useState(preselectedFirmwareId);
  const [targetType, setTargetType] = useState<TargetType>(preselectedImeis.length > 0 ? "devices" : "all");
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const [selectedDeviceImeis, setSelectedDeviceImeis] = useState<string[]>(preselectedImeis);
  const [selectedDeviceTenantByImei, setSelectedDeviceTenantByImei] = useState<Record<string, string>>({});
  const [groupSearch, setGroupSearch] = useState("");
  const [rolloutStrategy, setRolloutStrategy] = useState("immediate");
  const [batchSize, setBatchSize] = useState(50);
  const [batchInterval, setBatchInterval] = useState(30);
  const [failureThreshold, setFailureThreshold] = useState(10);
  const [description, setDescription] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (preselectedImeis.length > 0) {
      setTargetType("devices");
      setSelectedDeviceImeis(preselectedImeis);
    }
  }, [preselectedImeis]);

  useEffect(() => {
    if (!preselectedFirmwareId) return;
    setFirmwareId(preselectedFirmwareId);
  }, [preselectedFirmwareId]);

  const steps: Step[] = ["firmware", "target", "config", "review"];
  const firmware = useMemo(() => firmwareData?.data ?? [], [firmwareData?.data]);
  const existingTasks = useMemo(
    () => taskPicker.data?.pages.flatMap((page) => page.data) ?? [],
    [taskPicker.data?.pages],
  );
  const groups = useMemo(() => groupsData?.data ?? [], [groupsData?.data]);
  const devices = useMemo<TaskTargetDevice[]>(
    () => (isPlatformAdminTask ? adminDevicesData?.data ?? [] : devicesData?.data ?? []),
    [adminDevicesData?.data, devicesData?.data, isPlatformAdminTask],
  );
  const devicesLoading = isPlatformAdminTask ? adminDevicesLoading : tenantDevicesLoading;
  const devicePagination = isPlatformAdminTask ? adminDevicesData?.pagination : devicesData?.pagination;
  const devicePageStale = isPlatformAdminTask ? adminDevicesStale : tenantDevicesStale;
  const deviceTotalQuery = isPlatformAdminTask ? adminDeviceTotal : tenantDeviceTotal;
  const deviceTotal = deviceTotalQuery.isPlaceholderData ? undefined : deviceTotalQuery.data?.total;
  const deviceTotalIsLowerBound = !deviceTotalQuery.isPlaceholderData && !!deviceTotalQuery.data?.isLowerBound;
  const selectedFw = firmware.find((f) => f.firmwareId === firmwareId);
  const selectedExistingTask = existingTasks.find((task) => task.taskId === existingTaskId);

  useEffect(() => {
    if (taskSource !== "existing" || existingTaskId || existingTasks.length === 0) return;
    setExistingTaskId(existingTasks[0].taskId);
  }, [existingTaskId, existingTasks, taskSource]);

  useEffect(() => {
    if (!preselectedFirmwareId || !selectedFw || name) return;
    setName(`Rollout ${selectedFw.version} to devices`);
  }, [name, preselectedFirmwareId, selectedFw]);

  useEffect(() => {
    if (!isResourceTask || !resourceBundle || name) return;
    setName(`Deploy ${resourceBundle.name} v${resourceBundleVersion ?? resourceBundle.latestVersion} to devices`);
  }, [isResourceTask, name, resourceBundle, resourceBundleVersion]);

  const normalizedGroupSearch = groupSearch.trim().toLowerCase();

  const filteredGroups = groups.filter((group) =>
    !normalizedGroupSearch
      || includesQuery(group.name, normalizedGroupSearch)
      || includesQuery(group.description, normalizedGroupSearch)
      || includesQuery(group.groupId, normalizedGroupSearch),
  );

  const deviceByImei = useMemo(() => new Map(devices.map((device) => [device.imei, device])), [devices]);
  const unknownSelectedImeis = selectedDeviceImeis.filter((imei) => !deviceByImei.has(imei));

  useEffect(() => {
    setSelectedDeviceTenantByImei((current) => {
      const next: Record<string, string> = {};
      for (const imei of selectedDeviceImeis) {
        const tenantId = deviceByImei.get(imei)?.tenantId ?? current[imei];
        if (tenantId) next[imei] = tenantId;
      }
      return next;
    });
  }, [deviceByImei, selectedDeviceImeis]);

  const tableDevices = useMemo<TaskTargetDevice[]>(() => {
    const selected = new Set(selectedDeviceImeis);
    const knownSelected = devices.filter((device) => selected.has(device.imei));
    const unknownSelected = selectedDeviceImeis
      .filter((imei) => !deviceByImei.has(imei))
      .map((imei) => ({
        imei,
        deviceName: imei,
        status: "inactive",
        model: "Selected from previous page",
        tenantId: selectedDeviceTenantByImei[imei],
        createdAt: "",
        updatedAt: "",
      }));
    const unselected = devices.filter((device) => !selected.has(device.imei));
    return [...knownSelected, ...unknownSelected, ...unselected];
  }, [deviceByImei, devices, selectedDeviceImeis, selectedDeviceTenantByImei]);
  const selectedDeviceTenantIds = useMemo(
    () => Array.from(new Set(selectedDeviceImeis.map((imei) => deviceByImei.get(imei)?.tenantId ?? selectedDeviceTenantByImei[imei]).filter((tenantId): tenantId is string => !!tenantId))),
    [deviceByImei, selectedDeviceImeis, selectedDeviceTenantByImei],
  );
  const createTaskTenantId = scopedTenantId ?? (selectedDeviceTenantIds.length === 1 ? selectedDeviceTenantIds[0] : undefined);
  const createTask = useCreateTask(createTaskTenantId);
  const targetIds = targetType === "groups" ? selectedGroupIds : targetType === "devices" ? selectedDeviceImeis : [];
  const targetTenantReady = !isPlatformAdminTask || targetType !== "devices" || selectedDeviceTenantIds.length <= 1;
  const targetReady = (targetType === "all" || targetIds.length > 0) && targetTenantReady;
  const targetSummary = getTargetSummary(targetType, selectedGroupIds, selectedDeviceImeis, groups, deviceByImei);
  const selectedDevicesForSummary = selectedDeviceImeis.map((imei) => ({ imei, device: deviceByImei.get(imei) }));
  const deviceColumns = useMemo(() => {
    const columns = [
      {
        key: "status",
        header: "Status",
        sortable: true,
        render: (device: TaskTargetDevice) => <StatusBadge status={device.status ?? "inactive"} variant="dot" />,
      },
      {
        key: "lastSeen",
        header: "Last Seen",
        sortable: true,
        render: (device: TaskTargetDevice) => device.lastSeen ? new Date(device.lastSeen).toLocaleString() : "-",
      },
      {
        key: "deviceName",
        header: "Name",
        sortable: true,
        render: (device: TaskTargetDevice) => device.deviceName || device.imei,
      },
      {
        key: "contactNumber",
        header: "Contact",
        sortable: true,
        render: (device: TaskTargetDevice) => device.contactNumber || "-",
      },
      {
        key: "firmwareVersion",
        header: "Firmware",
        sortable: true,
        render: (device: TaskTargetDevice) => device.firmwareVersion || "-",
      },
      {
        key: "groupId",
        header: "Group",
        sortable: true,
        render: (device: TaskTargetDevice) => device.groupId || "-",
      },
      {
        key: "deviceInfo",
        header: "Device info",
        render: (device: TaskTargetDevice) => (
          <span className="block text-xs leading-5">
            <span className="block">IMEI: <span className="font-medium">{device.imei}</span></span>
            <span className="block text-gray-500 dark:text-riot-navy-400">Model: {device.model || "-"}</span>
          </span>
        ),
      },
    ];

    if (isPlatformAdminTask) {
      columns.splice(5, 0, {
        key: "tenantId",
        header: "Tenant",
        sortable: true,
        render: (device: TaskTargetDevice) => device.tenantId || "-",
      });
    }

    return columns;
  }, [isPlatformAdminTask]);

  function reviewExistingTask() {
    if (!selectedExistingTask) {
      setError("Choose an existing task first.");
      return;
    }
    setError("");
    setName(`${selectedExistingTask.name} - selected devices`);
    setFirmwareId(selectedExistingTask.firmwareId ?? "");
    setTargetType("devices");
    setSelectedDeviceImeis(preselectedImeis);
    setSelectedGroupIds([]);
    setRolloutStrategy(selectedExistingTask.rolloutStrategy || "immediate");
    setBatchSize(selectedExistingTask.stagedConfig?.batchSize ?? 50);
    setBatchInterval(selectedExistingTask.stagedConfig?.batchInterval ?? 30);
    setFailureThreshold(selectedExistingTask.stagedConfig?.failureThreshold ?? 10);
    setDescription(selectedExistingTask.description ?? "");
    setStep("target");
  }

  async function handleCreate() {
    setError("");
    if (isPlatformAdminTask && targetType === "devices" && selectedDeviceTenantIds.length > 1) {
      setError("Select devices from one company before creating the task.");
      setStep("target");
      return;
    }

    const stagedConfig: StagedConfig | undefined =
      rolloutStrategy === "staged"
        ? { batchSize, batchInterval, failureThreshold }
        : undefined;

    try {
      const res = await createTask.mutateAsync({
        name,
        ...(isResourceTask
          ? { taskType: "resource_update" as const, resourceBundleId: preselectedResourceBundleId, resourceBundleVersion }
          : { firmwareId }),
        targetType,
        targetIds: targetType !== "all" ? targetIds : undefined,
        rolloutStrategy,
        stagedConfig,
        description: description || undefined,
      });
      navigate(`/tasks/${res.data.taskId}`);
    } catch (err) {
      setError(extractErrorMessage(err, "Failed to create task"));
    }
  }

  function goToNextDevicePage() {
    if (!devicePagination?.cursor || !devicePagination.hasMore || devicePageStale) return;
    devicePaging.next(devicePagination.cursor, devices.length);
  }

  return (
    <div>
      <button onClick={() => navigate("/tasks")} className="mb-4 text-sm text-riot-gold-500 hover:underline">
        &larr; Back to tasks
      </button>

      <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-riot-navy-700 dark:bg-riot-navy-800">
        <h2 className="mb-4 text-lg font-semibold">{isResourceTask ? "Create Resource Update Task" : "Create Firmware Update Task"}</h2>

        <div className="mb-6 flex flex-wrap gap-2 text-xs">
          {steps.map((s, i) => (
            <span key={s} className={`rounded px-2 py-1 ${step === s ? "bg-riot-gold-100 text-riot-gold-600 font-medium dark:bg-riot-gold-500/20 dark:text-riot-gold-400" : "bg-gray-100 text-gray-400 dark:bg-riot-navy-700 dark:text-riot-navy-400"}`}>
              {i + 1}. {s.charAt(0).toUpperCase() + s.slice(1)}
            </span>
          ))}
        </div>

        {step === "firmware" && (
          <div className="space-y-4">
            {preselectedImeis.length > 0 && (
              <div className="rounded-md border border-riot-gold-500/30 bg-riot-gold-500/10 p-3 text-sm text-gray-700 dark:text-riot-navy-100">
                <div className="font-medium text-gray-900 dark:text-white">
                  {preselectedImeis.length} selected device{preselectedImeis.length === 1 ? "" : "s"}
                </div>
                <div className="mt-1 text-xs text-gray-500 dark:text-riot-navy-300">
                  {preselectedFirmwareId
                    ? "Review the selected firmware and create a rollout task for these devices."
                    : "Choose an existing task to apply the same firmware rollout setup, or create a new task on the fly."}
                </div>
              </div>
            )}

            {preselectedImeis.length > 0 && !preselectedFirmwareId && (
              <div>
                <label className="mb-2 block text-sm font-medium">Task setup</label>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <button
                    type="button"
                    aria-pressed={taskSource === "existing"}
                    onClick={() => setTaskSource("existing")}
                    className={`rounded-md border px-3 py-2 text-left text-sm font-medium ${taskSource === "existing" ? "border-riot-gold-400 bg-riot-gold-400 text-riot-navy-950" : "border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-riot-navy-600 dark:text-riot-navy-100 dark:hover:bg-riot-navy-700"}`}
                  >
                    Choose existing task
                  </button>
                  <button
                    type="button"
                    aria-pressed={taskSource === "new"}
                    onClick={() => setTaskSource("new")}
                    className={`rounded-md border px-3 py-2 text-left text-sm font-medium ${taskSource === "new" ? "border-riot-gold-400 bg-riot-gold-400 text-riot-navy-950" : "border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-riot-navy-600 dark:text-riot-navy-100 dark:hover:bg-riot-navy-700"}`}
                  >
                    Create task on the fly
                  </button>
                </div>
              </div>
            )}

            {preselectedImeis.length > 0 && !preselectedFirmwareId && taskSource === "existing" ? (
              <div className="space-y-4">
                <div>
                  <label className="mb-1 block text-sm font-medium">Existing task</label>
                  <select
                    aria-label="Existing Task"
                    value={existingTaskId}
                    onChange={(e) => setExistingTaskId(e.target.value)}
                    className="w-full rounded border border-gray-300 px-3 py-2 text-sm dark:border-riot-navy-600 dark:bg-riot-navy-800 dark:text-white"
                  >
                    <option value="">{tasksLoading ? "Loading tasks..." : "Select existing task..."}</option>
                    {existingTasks.map((task) => (
                      <option key={task.taskId} value={task.taskId}>
                        {task.name} - {task.rolloutStrategy} - {task.firmwareId}
                      </option>
                    ))}
                  </select>
                  {!tasksLoading && existingTasks.length === 0 && (
                    <p className="mt-1 text-xs text-gray-500 dark:text-riot-navy-300">
                      No existing tasks yet. Create a new task on the fly instead.
                    </p>
                  )}
                  {taskPicker.hasNextPage && (
                    <button
                      type="button"
                      onClick={() => taskPicker.fetchNextPage()}
                      disabled={taskPicker.isFetchingNextPage}
                      className="mt-1 text-xs font-medium text-riot-gold-600 hover:text-riot-gold-700 disabled:opacity-50 dark:text-riot-gold-400"
                    >
                      {taskPicker.isFetchingNextPage
                        ? "Loading more tasks..."
                        : `Showing ${existingTasks.length.toLocaleString()} tasks — load more`}
                    </button>
                  )}
                </div>
                {selectedExistingTask && (
                  <dl className="grid grid-cols-1 gap-3 rounded-md border border-gray-200 p-3 text-xs dark:border-riot-navy-700 sm:grid-cols-3">
                    <div><dt className="text-gray-500 dark:text-riot-navy-300">Firmware</dt><dd className="mt-0.5 font-medium">{selectedExistingTask.firmwareId}</dd></div>
                    <div><dt className="text-gray-500 dark:text-riot-navy-300">Strategy</dt><dd className="mt-0.5 font-medium capitalize">{selectedExistingTask.rolloutStrategy}</dd></div>
                    <div><dt className="text-gray-500 dark:text-riot-navy-300">Previous target</dt><dd className="mt-0.5 font-medium">{selectedExistingTask.totalDevices} devices</dd></div>
                  </dl>
                )}
                {error && <p className="text-sm text-red-600">{error}</p>}
                <button
                  disabled={!selectedExistingTask || preselectedImeis.length === 0}
                  onClick={reviewExistingTask}
                  className="rounded bg-riot-gold-400 px-4 py-2 text-sm text-riot-navy-900 hover:bg-riot-gold-500 active:bg-riot-gold-600 disabled:opacity-50"
                >
                  Continue to targets
                </button>
              </div>
            ) : (
              <>
            <div>
              <label className="mb-1 block text-sm font-medium">Task Name</label>
              <input aria-label="Task Name" type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. First firmware update" className="w-full rounded border border-gray-300 px-3 py-2 text-sm dark:border-riot-navy-600 dark:bg-riot-navy-800 dark:text-white" />
            </div>
            {isResourceTask ? (
              <div>
                <label className="mb-1 block text-sm font-medium">Resource Bundle</label>
                <div className="rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm dark:border-riot-navy-700 dark:bg-riot-navy-900/60">
                  {resourceBundle ? (
                    <span>
                      {resourceBundle.name} ({resourceBundle.model}) &mdash; version {resourceBundleVersion}
                    </span>
                  ) : (
                    <span className="text-gray-400 dark:text-riot-navy-400">Loading resource bundle...</span>
                  )}
                </div>
              </div>
            ) : (
              <div>
                <label className="mb-1 block text-sm font-medium">Target Firmware</label>
                <select aria-label="Target Firmware" value={firmwareId} onChange={(e) => setFirmwareId(e.target.value)} className="w-full rounded border border-gray-300 px-3 py-2 text-sm dark:border-riot-navy-600 dark:bg-riot-navy-800 dark:text-white">
                  <option value="">Select firmware...</option>
                  {firmware.map((f) => (
                    <option key={f.firmwareId} value={f.firmwareId}>
                      {f.version} - {f.model} ({f.filename})
                    </option>
                  ))}
                </select>
                {preselectedFirmwareId && firmware.length > 0 && !selectedFw && (
                  <p className="mt-1 text-xs text-red-600 dark:text-red-300">
                    This firmware is not currently available for rollout tasks.
                  </p>
                )}
              </div>
            )}
            <button
              disabled={!name || (isResourceTask ? !resourceBundle : !firmwareId)}
              onClick={() => setStep("target")}
              className="rounded bg-riot-gold-400 px-4 py-2 text-sm text-riot-navy-900 hover:bg-riot-gold-500 active:bg-riot-gold-600 disabled:opacity-50"
            >
              Next: Choose Targets
            </button>
              </>
            )}
          </div>
        )}

        {step === "target" && (
          <div className="space-y-4">
            <div>
              <label className="mb-2 block text-sm font-medium">Apply task to</label>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                {[
                  ["all", "All devices"],
                  ["groups", "Device groups"],
                  ["devices", "Devices"],
                ].map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    aria-pressed={targetType === value}
                    onClick={() => setTargetType(value as TargetType)}
                    className={`rounded-md border px-3 py-2 text-left text-sm font-medium ${targetType === value ? "border-riot-gold-400 bg-riot-gold-400 text-riot-navy-950" : "border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-riot-navy-600 dark:text-riot-navy-100 dark:hover:bg-riot-navy-700"}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {targetType === "groups" && (
              <TargetPicker
                label="Device groups"
                searchLabel="Search groups"
                searchValue={groupSearch}
                onSearch={setGroupSearch}
                isLoading={groupsLoading}
                emptyText="No device groups found"
              >
                {filteredGroups.map((group) => (
                  <GroupRow
                    key={group.groupId}
                    group={group}
                    checked={selectedGroupIds.includes(group.groupId)}
                    onToggle={() => setSelectedGroupIds((current) => toggleValue(current, group.groupId))}
                  />
                ))}
              </TargetPicker>
            )}

            {targetType === "devices" && (
              <div className="space-y-3">
                <div className="rounded-md border border-gray-200 bg-gray-50 p-3 dark:border-riot-navy-700 dark:bg-riot-navy-900/60">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white">Selected devices</div>
                      <div className="text-xs text-gray-500 dark:text-riot-navy-300">
                        {selectedDeviceImeis.length} device{selectedDeviceImeis.length === 1 ? "" : "s"} selected for this task
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {selectedDeviceImeis.length > 0 && (
                        <button
                          type="button"
                          onClick={() => setSelectedDeviceImeis([])}
                          className="inline-flex items-center gap-1 rounded border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-white dark:border-riot-navy-600 dark:text-riot-navy-100 dark:hover:bg-riot-navy-800"
                        >
                          <X size={14} aria-hidden="true" />
                          Clear
                        </button>
                      )}
                    </div>
                  </div>

                  {selectedDeviceImeis.length === 0 ? (
                    <p className="mt-3 text-sm text-gray-500 dark:text-riot-navy-300">No devices selected yet.</p>
                  ) : (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {selectedDevicesForSummary.map(({ imei, device }) => (
                        <span
                          key={imei}
                          className="inline-flex max-w-full items-center gap-2 rounded border border-gray-300 bg-white px-2.5 py-1.5 text-xs text-gray-700 dark:border-riot-navy-600 dark:bg-riot-navy-800 dark:text-riot-navy-100"
                        >
                          <span className="truncate">{deviceLabel(device, imei)}</span>
                          <button
                            type="button"
                            onClick={() => setSelectedDeviceImeis((current) => current.filter((entry) => entry !== imei))}
                            className="text-gray-400 hover:text-red-500 dark:text-riot-navy-400"
                            aria-label={`Remove ${imei}`}
                          >
                            <X size={13} aria-hidden="true" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {unknownSelectedImeis.length > 0 && (
                  <p className="text-xs text-amber-600 dark:text-amber-300">
                    {unknownSelectedImeis.length} selected device{unknownSelectedImeis.length === 1 ? "" : "s"} came from the previous page and may not be visible in this table page.
                  </p>
                )}

                {!targetTenantReady && (
                  <p className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-600 dark:text-red-300">
                    Select devices from one company before continuing.
                  </p>
                )}

                <DataTable<TaskTargetDevice>
                  data={tableDevices}
                  columns={deviceColumns}
                  keyExtractor={(device) => device.imei}
                  selectable
                  selectedKeys={selectedDeviceImeis}
                  onSelectionChange={setSelectedDeviceImeis}
                  title="Devices"
                  totalCount={deviceTotal}
                  serverSide
                  emptyMessage={
                    devicePagination?.hasMore && devices.length === 0 && !devicePageStale ? (
                      <span>
                        No matches in the devices checked so far.{" "}
                        <button type="button" onClick={goToNextDevicePage} className="font-medium text-riot-gold-600 hover:text-riot-gold-700 dark:text-riot-gold-400">
                          Keep searching
                        </button>
                      </span>
                    ) : undefined
                  }
                  onFilterChange={(f) => {
                    setDeviceSearch(f.search);
                    setDeviceSearchField(f.searchField as DeviceSearchField);
                    setDeviceStatus(f.status);
                    setUngroupedOnly(!!f.checkbox.groupId);
                  }}
                  actions={
                    <span className="text-xs text-gray-500 dark:text-riot-navy-300">
                      {devicesLoading ? "Loading devices..." : `${selectedDeviceImeis.length} selected`}
                    </span>
                  }
                  filters={{
                    searchPlaceholder: "Search by",
                    searchFields: [
                      { value: "imei", label: "IMEI" },
                      { value: "deviceName", label: "Name" },
                      { value: "contactNumber", label: "Contact" },
                      { value: "firmwareVersion", label: "Firmware" },
                      { value: "model", label: "Model" },
                    ],
                    statusLabel: "Status",
                    // The statuses the list shows (derived from last-seen time).
                    statusOptions: [
                      { value: "online", label: "Online" },
                      { value: "stale", label: "Stale" },
                      { value: "offline", label: "Offline" },
                    ],
                    checkboxFilters: [{ key: "groupId", label: "Ungrouped", mode: "missing" }],
                  }}
                  pagination={devicePagination ? {
                    page: devicePaging.page,
                    totalPages: totalPagesFor(devicePaging.page, DEVICE_TARGET_PAGE_SIZE, devicePagination.hasMore, deviceTotal),
                    count: devices.length,
                    hasMore: devicePagination.hasMore,
                    rangeLabel: formatRange(devicePaging.offset, devices.length, deviceTotal, deviceTotalIsLowerBound),
                    onNext: goToNextDevicePage,
                    onPrev: devicePaging.prev,
                    onFirst: devicePaging.first,
                  } : undefined}
                />
              </div>
            )}

            <div className="flex gap-2">
              <button onClick={() => setStep("firmware")} className="rounded border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50 dark:hover:bg-riot-navy-700">Back</button>
              <button disabled={!targetReady} onClick={() => setStep("config")} className="rounded bg-riot-gold-400 px-4 py-2 text-sm text-riot-navy-900 hover:bg-riot-gold-500 active:bg-riot-gold-600 disabled:opacity-50">Next: Configure</button>
            </div>
          </div>
        )}

        {step === "config" && (
          <div className="space-y-4">
            <div>
              <label className="mb-1 block text-sm font-medium">Rollout Strategy</label>
              <select value={rolloutStrategy} onChange={(e) => setRolloutStrategy(e.target.value)} className="w-full rounded border border-gray-300 px-3 py-2 text-sm dark:border-riot-navy-600 dark:bg-riot-navy-800 dark:text-white">
                <option value="immediate">Immediate (all devices at once)</option>
                <option value="staged">Staged (batched rollout)</option>
              </select>
            </div>
            {rolloutStrategy === "staged" && (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div>
                  <label className="mb-1 block text-xs text-gray-600 dark:text-riot-navy-300">Batch Size</label>
                  <input type="number" value={batchSize} onChange={(e) => setBatchSize(Number(e.target.value))} min={1} className="w-full rounded border border-gray-300 px-3 py-2 text-sm dark:border-riot-navy-600 dark:bg-riot-navy-800 dark:text-white" />
                </div>
                <div>
                  <label className="mb-1 block text-xs text-gray-600 dark:text-riot-navy-300">Interval (min)</label>
                  <input type="number" value={batchInterval} onChange={(e) => setBatchInterval(Number(e.target.value))} min={1} className="w-full rounded border border-gray-300 px-3 py-2 text-sm dark:border-riot-navy-600 dark:bg-riot-navy-800 dark:text-white" />
                </div>
                <div>
                  <label className="mb-1 block text-xs text-gray-600 dark:text-riot-navy-300">Failure Threshold (%)</label>
                  <input type="number" value={failureThreshold} onChange={(e) => setFailureThreshold(Number(e.target.value))} min={0} max={100} className="w-full rounded border border-gray-300 px-3 py-2 text-sm dark:border-riot-navy-600 dark:bg-riot-navy-800 dark:text-white" />
                </div>
              </div>
            )}
            <div>
              <label className="mb-1 block text-sm font-medium">Description (optional)</label>
              <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className="w-full rounded border border-gray-300 px-3 py-2 text-sm dark:border-riot-navy-600 dark:bg-riot-navy-800 dark:text-white" />
            </div>
            <div className="flex gap-2">
              <button onClick={() => setStep("target")} className="rounded border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50 dark:hover:bg-riot-navy-700">Back</button>
              <button onClick={() => setStep("review")} className="rounded bg-riot-gold-400 px-4 py-2 text-sm text-riot-navy-900 hover:bg-riot-gold-500 active:bg-riot-gold-600">Review</button>
            </div>
          </div>
        )}

        {step === "review" && (
          <div className="space-y-4">
            <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
              <div><dt className="text-gray-500 dark:text-riot-navy-300">Name</dt><dd className="font-medium">{name}</dd></div>
              {isResourceTask ? (
                <div><dt className="text-gray-500 dark:text-riot-navy-300">Resource Bundle</dt><dd>{resourceBundle ? `${resourceBundle.name} v${resourceBundleVersion}` : preselectedResourceBundleId}</dd></div>
              ) : (
                <div><dt className="text-gray-500 dark:text-riot-navy-300">Firmware</dt><dd>{selectedFw ? `${selectedFw.version} (${selectedFw.model})` : firmwareId}</dd></div>
              )}
              <div><dt className="text-gray-500 dark:text-riot-navy-300">Target</dt><dd>{targetSummary}</dd></div>
              <div><dt className="text-gray-500 dark:text-riot-navy-300">Strategy</dt><dd>{rolloutStrategy}</dd></div>
            </dl>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <div className="flex gap-2">
              <button onClick={() => setStep("config")} className="rounded border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50 dark:hover:bg-riot-navy-700">Back</button>
              <button onClick={handleCreate} disabled={createTask.isPending} className="rounded-md bg-riot-gold-400 px-6 py-2 text-sm font-medium text-riot-navy-900 hover:bg-riot-gold-500 active:bg-riot-gold-600 disabled:opacity-50">
                {createTask.isPending ? "Creating..." : "Create Task"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function TargetPicker({
  label,
  searchLabel,
  searchValue,
  onSearch,
  isLoading,
  emptyText,
  children,
}: {
  label: string;
  searchLabel: string;
  searchValue: string;
  onSearch: (value: string) => void;
  isLoading: boolean;
  emptyText: string;
  children: ReactNode;
}) {
  const isEmpty = !isLoading && Children.count(children) === 0;

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <label className="block text-sm font-medium">{label}</label>
      </div>
      <div className="mb-2 flex max-w-md items-center gap-2 rounded border border-gray-300 px-3 py-2 dark:border-riot-navy-600 dark:bg-riot-navy-900">
        <Search size={16} className="text-gray-400 dark:text-riot-navy-400" />
        <input
          aria-label={searchLabel}
          value={searchValue}
          onChange={(e) => onSearch(e.target.value)}
          placeholder={searchLabel}
          className="w-full bg-transparent text-sm outline-none dark:text-white"
        />
      </div>
      <div className="max-h-72 overflow-y-auto rounded-md border border-gray-200 dark:border-riot-navy-700">
        {isLoading && <p className="px-4 py-6 text-sm text-gray-400 dark:text-riot-navy-400">Loading...</p>}
        {isEmpty && <p className="px-4 py-6 text-sm text-gray-400 dark:text-riot-navy-400">{emptyText}</p>}
        {children}
      </div>
    </div>
  );
}

function GroupRow({ group, checked, onToggle }: { group: DeviceGroup; checked: boolean; onToggle: () => void }) {
  return (
    <label className="flex cursor-pointer items-center gap-3 border-b border-gray-100 px-4 py-3 last:border-0 hover:bg-gray-50 dark:border-riot-navy-700 dark:hover:bg-riot-navy-700/60">
      <input type="checkbox" checked={checked} onChange={onToggle} className="h-4 w-4 rounded border-gray-300 text-riot-gold-500 focus:ring-riot-gold-500" />
      <span className="min-w-0">
        <span className="block truncate text-sm font-medium text-gray-900 dark:text-white">{group.name}</span>
        <span className="block truncate text-xs text-gray-500 dark:text-riot-navy-400">{group.deviceCount} devices • {group.type}</span>
      </span>
    </label>
  );
}

function getTargetSummary(
  targetType: TargetType,
  selectedGroupIds: string[],
  selectedDeviceImeis: string[],
  groups: DeviceGroup[],
  deviceByImei: Map<string, Device>,
): string {
  if (targetType === "all") return "All devices";
  if (targetType === "groups") {
    const names = selectedGroupIds.map((id) => groups.find((group) => group.groupId === id)?.name ?? id);
    return `${names.length} device group${names.length === 1 ? "" : "s"}: ${names.join(", ")}`;
  }
  const labels = selectedDeviceImeis.map((imei) => deviceLabel(deviceByImei.get(imei), imei));
  return `${labels.length} device${labels.length === 1 ? "" : "s"}: ${labels.join(", ")}`;
}
